"use client";

import type { ReviewCarouselVariant } from "./ReviewCarouselTitle";

import { cn } from "../utils/classnames";
import { formatAge } from "../utils/format-age";
import { Carousel, CarouselContent, CarouselItem } from "./Carousel";
import { AvatarBackgroundVariants, ReviewCard } from "./ReviewCard";
import { ReviewCarouselTitle } from "./ReviewCarouselTitle";

export type { ReviewCarouselVariant };

export type ReviewItem = {
  name: string;
  rating: number;
  date?: string;
  text: string;
};

type ReviewCarouselProps = {
  className?: string;
  title: string;
  reviews: ReviewItem[];
  variant?: ReviewCarouselVariant;
  /**
   * Set when the wrapping PageBuilder section hangs its content over the
   * next section. This carousel paints its own full-bleed background
   * (independent of the section's `data-surface`), so the hang is marked on
   * the title+cards element rather than the outer bleed wrapper -- otherwise
   * the bleed background would shift down with it and keep covering the
   * seam. See `data-section-overlap-hang` in `apps/hi-www/src/app/globals.css`.
   */
  overlapsNext?: boolean;
};

const ReviewCarousel = ({
  className,
  overlapsNext,
  title,
  reviews,
  variant = "on-dark",
}: ReviewCarouselProps) => {
  if (!reviews || reviews.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "relative right-1/2 left-1/2 -mx-[50vw] w-screen pt-8",
        { "bg-new-beginnings-base": variant === "on-dark" },
        className,
      )}
    >
      <Carousel data-section-overlap-hang={overlapsNext ? "" : undefined}>
        <ReviewCarouselTitle
          reviewsLength={reviews.length}
          title={title}
          variant={variant}
        />

        {/* Carousel content */}
        <CarouselContent className="mx-4 gap-4 sm:mx-10 lg:mx-[72px] 2xl:mx-[152px]">
          {reviews.map((review, index) => (
            <CarouselItem className="mb-4 max-w-[368px] px-0" key={index}>
              <ReviewCard
                avatarClassName={
                  AvatarBackgroundVariants[
                    index % AvatarBackgroundVariants.length
                  ]
                }
                className="h-[352px] w-[368px]"
                date={formatAge(review.date)}
                initials={(review.name || "A").charAt(0)}
                name={review.name || "Anonymous"}
                rating={review.rating}
                review={review.text}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
};

export default ReviewCarousel;
